import {
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Table as TableType,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { TableSkeleton } from '@/components/skeletons'
import { createTableFilterFn, usePagination } from '@/stores'
import { cn } from '@/utils'
import { BulkActionsToolbar } from './BulkActionsToolbar'
import { DataTableHeaders } from './DataTableHeaders'
import { DataTableRow } from './DataTableRow'
import { TableControls } from './TableControls'
import type { DataTableColumns, Filter, TableData } from './types'

const defaultColumn = {
  size: 150,
  minSize: 50,
  maxSize: 1000,
}

interface DataTableProps {
  columns: DataTableColumns
  data: TableData[]
  height?: string
  onSelectionChange?: (selection: RowSelectionState) => void
  onTableReady?: (table: TableType<TableData>) => void
  paginationKey?: string
  filters?: Filter[]
  enableRowSelection?: boolean | ((row: Row<any>) => boolean) | undefined
  filterSchema?: any
  globalFilter?: string
  scrollbarClassName?: string
  bulkActions?: (rowSelection: RowSelectionState) => React.ReactNode
  isLoading?: boolean
  emptyMessage?: string
  onRowClick?: (row: Row<TableData>) => void
  pageSizes?: number[]
  initialPageSize?: number
  // Rendered between the table body and the pagination controls, e.g. a
  // grand-total row that belongs to the whole dataset, not the page.
  footer?: React.ReactNode
  // Pinned under the rows inside the scroll area, so it scrolls sideways with the columns.
  stickyFooter?: React.ReactNode
  // Render extra content below a row's cells when it is expanded. Clicking an expandable row toggles
  // it; the virtualizer re-measures the taller row on its own.
  renderSubComponent?: (row: Row<TableData>) => React.ReactNode
  getRowCanExpand?: (row: Row<TableData>) => boolean
  // An action that floats over the right edge of every row and stays there while the table scrolls
  // sideways, so it is in reach however many columns sit in front of it.
  rowAction?: (row: Row<TableData>) => React.ReactNode
}

export const DataTable = ({
  columns,
  data,
  height = 'h-[500px]',
  onSelectionChange,
  onTableReady,
  paginationKey,
  emptyMessage = 'No results.',
  filters,
  filterSchema,
  globalFilter,
  scrollbarClassName,
  bulkActions,
  isLoading = false,
  enableRowSelection = true,
  onRowClick,
  pageSizes,
  initialPageSize,
  footer,
  stickyFooter,
  renderSubComponent,
  getRowCanExpand,
  rowAction,
}: DataTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [expanded, setExpanded] = useState<ExpandedState>({})

  const columnVisibility = useMemo<VisibilityState>(
    () =>
      columns.reduce((acc, col) => {
        if ('accessorKey' in col && !col.header) {
          acc[col.accessorKey as string] = false
        }
        return acc
      }, {} as VisibilityState),
    [columns],
  )

  const pagination = usePagination(paginationKey, initialPageSize)

  const columnFilters = useMemo(() => {
    if (!filters || filters.length === 0) return []

    return filters
      .filter((filter: Filter) => filter.field || filter.column)
      .map((filter: Filter) => ({
        id: (filter.field || filter.column)!,
        value: filter,
      }))
  }, [filters])

  // `createTableFilterFn` returns a fresh closure each call; without
  // memoization it would invalidate `filterFns` on every render and bust
  // tanstack's internal filtered-row-model memoization. Stable per
  // schema so the table reuses its filtered output across renders.
  const filterFn = useMemo(
    () => createTableFilterFn(filterSchema),
    [filterSchema],
  )

  const table = useReactTable({
    data,
    columns,
    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onPaginationChange: pagination.actions.setPagination,
    state: {
      sorting,
      rowSelection,
      expanded,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
      columnFilters,
      columnVisibility,
      globalFilter,
    },
    meta: { searchQuery: globalFilter },
    columnResizeMode: 'onChange',
    defaultColumn,
    enableRowSelection,
    autoResetPageIndex: false,
    filterFns: {
      custom: filterFn,
    },
  })

  const setPage = pagination.actions.setPage
  useEffect(() => {
    setPage(0)
  }, [setPage, globalFilter, columnFilters])

  useEffect(() => {
    onSelectionChange?.(rowSelection)
  }, [rowSelection, onSelectionChange])

  const tableReadyCalledRef = useRef(false)
  useEffect(() => {
    if (!tableReadyCalledRef.current && table) {
      tableReadyCalledRef.current = true
      onTableReady?.(table)
    }
  }, [table, onTableReady])

  const { rows } = table.getRowModel()
  const parentRef = useRef<HTMLDivElement>(null)

  // Measurements must follow the row, not its position: rows shift index on live
  // updates/sorting, and index-keyed height caches paint rows overlapped until a
  // scroll or hover forces a re-measure. Read rows through a ref so getItemKey keeps
  // one identity — the virtualizer notifies (re-renders) whenever it changes.
  const rowsRef = useRef(rows)
  rowsRef.current = rows
  const getItemKey = useCallback(
    (index: number) => rowsRef.current[index]?.id ?? index,
    [],
  )

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    getItemKey,
    overscan: 5,
  })

  useLayoutEffect(() => {
    if (!parentRef.current || isLoading) return

    const element = parentRef.current
    setContainerWidth(element.getBoundingClientRect().width)

    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0]?.contentRect.width ?? 0
      setContainerWidth((prev) =>
        Math.abs(newWidth - prev) > 1 ? newWidth : prev,
      )
    })
    observer.observe(element)

    return () => observer.disconnect()
  }, [isLoading])

  const totalWidth = table.getTotalSize()
  const [containerWidth, setContainerWidth] = useState(0)
  const tableWidth = Math.max(totalWidth, containerWidth)
  const virtualRows = rowVirtualizer.getVirtualItems()

  if (isLoading) {
    return <TableSkeleton />
  }

  return (
    <>
      <div
        className={cn('w-full overflow-hidden bg-white flex flex-col', height)}
        style={
          {
            '--table-width': `${tableWidth}px`,
            '--table-container-width': `${containerWidth}px`,
            '--table-body-height': `${rowVirtualizer.getTotalSize()}px`,
          } as CSSProperties
        }
      >
        <div
          ref={parentRef}
          className={cn(
            'w-full flex-1 min-h-0 overflow-auto',
            scrollbarClassName ||
              'scrollbar-thin scrollbar-thumb-neutral-300 hover:scrollbar-thumb-neutral-400',
          )}
        >
          <div className="min-w-(--table-width)">
            <div className="sticky top-0 z-10 border-b bg-blue-1">
              <DataTableHeaders table={table} />
            </div>
            <div
              className="relative w-(--table-width)"
              style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
            >
              {virtualRows
                .filter(({ index }) => rows[index])
                .map((virtualRow) => (
                  <DataTableRow
                    key={rows[virtualRow.index].id}
                    rows={rows}
                    rowVirtualizer={rowVirtualizer}
                    virtualRow={virtualRow}
                    onRowClick={onRowClick}
                    renderSubComponent={renderSubComponent}
                    rowAction={rowAction}
                  />
                ))}
            </div>
            {stickyFooter && (
              <div className="sticky bottom-0 z-10 border-t bg-card">
                {stickyFooter}
              </div>
            )}
          </div>
        </div>

        {bulkActions && (
          <BulkActionsToolbar
            selectedCount={Object.values(rowSelection).length}
            onClose={() => table.resetRowSelection()}
          >
            {bulkActions(rowSelection)}
          </BulkActionsToolbar>
        )}
      </div>
      {footer}
      {table && rows.length > 0 && (
        <TableControls
          table={table}
          pagination={pagination}
          alwaysVisible={!!paginationKey}
          pageSizes={pageSizes}
        />
      )}
      {rows.length === 0 && (
        <div className="text-center p-5">{emptyMessage}</div>
      )}
    </>
  )
}
